import { useState } from 'react';
import { Box } from '@chakra-ui/react';
import { Colors, Icon, Text } from '@mercator-gcp/grout';
import { capitalize } from 'lodash';
import AIMarker from 'components/elements/AIMarker';
import PopoverMenu from 'components/elements/PopoverMenu';
import { FlexRow } from 'components/layout/FlexRow';
import type { GroupConfigMinimal } from 'services/API/GroupConfigurationAPI';

const LABEL_TEXT = 'Viewing all projects';
const AI_SOURCE: GroupConfigMinimal['source'] = 'scoutbot';

const triggerStyle = {
  display: 'flex',
  width: '100%',
  height: '40px',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'gray.200',
  borderRadius: '6px',
  paddingX: 'padding_16',
  backgroundColor: Colors.White,
  cursor: 'pointer',
  justifyContent: 'space-between',
  alignItems: 'center',
  columnGap: 'padding_8',
  '&:focus': {
    borderColor: Colors.ForestGreen900,
    outline: 'none',
  },
};

const triggerLabelStyle = {
  flex: 1,
  minWidth: 0,
  justifyContent: 'flex-start',
  alignItems: 'center',
  columnGap: 'padding_8',
};

const aiMarkerWrapperStyle = {
  flexShrink: 0,
  display: 'flex',
};

const triggerTextStyle = {
  flex: 1,
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const placeholderTextStyle = {
  ...triggerTextStyle,
  color: Colors.TextQuiet,
};

// PopoverMenu's own popoverStyle forces `overflow: hidden` on the content
// (spread after any sx we pass), so the scroll has to live on an inner
// container it can't clobber.
const scrollContainerStyle = {
  maxHeight: '320px',
  overflowY: 'auto',
};

const itemStyle = {
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  columnGap: 'padding_8',
  paddingX: 'padding_8',
  paddingY: 'padding_8',
  cursor: 'pointer',
  '&:hover': {
    background: Colors.ArmyGreen100,
  },
};

type LeadGroupDropdownProps = {
  groups: GroupConfigMinimal[] | null | undefined;
  onSelect: (arg0: number) => void;
  selectedGroupId: string | null;
};

const SavedSearchDropdown = ({
  groups,
  selectedGroupId,
  onSelect,
}: LeadGroupDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectedGroup = groups?.find((group) => String(group.id) === selectedGroupId);

  const handleSelect = (groupId: number) => {
    onSelect(groupId);
    setIsOpen(false);
  };

  return (
    <PopoverMenu
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      placement="bottom-start"
    >
      <PopoverMenu.Trigger>
        <Box
          as="button"
          type="button"
          sx={triggerStyle}
          onClick={() => setIsOpen((prev) => !prev)}
        >
          <FlexRow sx={triggerLabelStyle}>
            {selectedGroup?.source === AI_SOURCE && (
              <Box sx={aiMarkerWrapperStyle}>
                <AIMarker labelOverride="This saved search was created by Scout AI" />
              </Box>
            )}
            <Text sx={selectedGroup ? triggerTextStyle : placeholderTextStyle}>
              {selectedGroup ? capitalize(selectedGroup.name) : LABEL_TEXT}
            </Text>
          </FlexRow>
          <Icon name={Icon.Name.ChevronDown} />
        </Box>
      </PopoverMenu.Trigger>
      <PopoverMenu.Content>
        <Box sx={scrollContainerStyle}>
          <Box onClick={() => handleSelect(0)} sx={itemStyle}>
            <Text>{LABEL_TEXT}</Text>
          </Box>
          {groups?.map((groupData) => (
            <Box
              key={groupData.id}
              onClick={() => handleSelect(groupData.id)}
              sx={itemStyle}
            >
              {groupData.source === AI_SOURCE && (
                <Box sx={aiMarkerWrapperStyle}>
                  <AIMarker labelOverride="This saved search was created by Scout AI" />
                </Box>
              )}
              <Text sx={triggerTextStyle}>{capitalize(groupData.name)}</Text>
            </Box>
          ))}
        </Box>
      </PopoverMenu.Content>
    </PopoverMenu>
  );
};

export default SavedSearchDropdown;
